import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Button,
  Paper,
  Chip,
  Divider,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  Analytics as AnalyticsIcon,
  TrendingUp as TrendingIcon,
  PieChart as ChartIcon,
  GetApp as ExportIcon,
  Speed as RealTimeIcon,
  FilterList as FilterIcon,
  CheckCircle as CheckIcon,
  Info as InfoIcon,
  Schedule as TimeIcon,
  People as PeopleIcon
} from '@mui/icons-material';
import Screenshot from './Screenshot';
import ScreenshotGallery from './ScreenshotGallery';
import chart from '../../assets/images/chart.webp';
import result from '../../assets/images/result.webp';

const ResponseAnalytics = () => {
  const analyticsFeatures = [
    {
      title: 'Real-time Dashboard',
      icon: <RealTimeIcon color="primary" />,
      description: 'Monitor responses as they come in with live updates and instant notifications.',
      features: ['Live response counter', 'Completion rate tracking', 'Response time analytics', 'Geographic distribution']
    },
    {
      title: 'Visual Charts & Graphs',
      icon: <ChartIcon color="primary" />,
      description: 'Automatically generated charts and visualizations for all question types.',
      features: ['Bar charts for multiple choice', 'Pie charts for distributions', 'Word clouds for text responses', 'Trend lines for time-based data']
    },
    {
      title: 'Advanced Filtering',
      icon: <FilterIcon color="primary" />,
      description: 'Filter and segment your data to uncover deeper insights.',
      features: ['Date range filtering', 'Response source filtering', 'Custom field filtering', 'Demographic segmentation']
    },
    {
      title: 'Export Options',
      icon: <ExportIcon color="primary" />,
      description: 'Export your data in multiple formats for further analysis.',
      features: ['CSV export', 'Excel export', 'PDF reports', 'API access']
    }
  ];

  const metricsData = [
    { metric: 'Total Responses', value: '1,247', change: '+12%', color: 'success' },
    { metric: 'Completion Rate', value: '78.5%', change: '+5.2%', color: 'success' },
    { metric: 'Average Time', value: '4m 32s', change: '-8s', color: 'info' },
    { metric: 'Drop-off Rate', value: '21.5%', change: '-3.1%', color: 'success' }
  ];

  const reportTypes = [
    {
      name: 'Summary Report',
      description: 'High-level overview with key metrics and trends',
      includes: ['Response counts', 'Completion rates', 'Top responses', 'Time trends'],
      bestFor: 'Executive summaries and quick overviews'
    },
    {
      name: 'Detailed Analysis',
      description: 'In-depth analysis with cross-tabulations and correlations',
      includes: ['Question-by-question breakdown', 'Cross-tabulations', 'Statistical significance', 'Correlation analysis'],
      bestFor: 'Research teams and detailed insights'
    },
    {
      name: 'Visual Dashboard',
      description: 'Interactive charts and graphs for presentations',
      includes: ['Interactive charts', 'Customizable layouts', 'Real-time updates', 'Presentation mode'],
      bestFor: 'Presentations and stakeholder meetings'
    },
    {
      name: 'Raw Data Export',
      description: 'Complete dataset for custom analysis',
      includes: ['All response data', 'Timestamps', 'Metadata', 'Custom formatting'],
      bestFor: 'Data scientists and custom analysis'
    }
  ];

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
        Response Analytics
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4,  }}>
        Discover powerful insights from your survey responses with SurveySlack's comprehensive 
        analytics and reporting tools.
      </Typography>

      {/* Overview */}
      <Alert severity="info" sx={{ mb: 4 }}>
        <Typography variant="body2">
          <strong>Analytics are updated in real-time</strong> as responses come in. You can start analyzing 
          data immediately after your first response is submitted.
        </Typography>
      </Alert>

      {/* Key Metrics Dashboard */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 500, mb: 3 }}>
          Key Metrics Dashboard
        </Typography>
        <Grid container spacing={3}>
          {metricsData.map((metric, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card sx={{ border: '1px solid', borderColor: 'divider', height: '100%' }}>
                <CardContent sx={{ textAlign: 'center', p: 3 }}>
                  <Typography variant="h4" sx={{ fontWeight: 600, color: `${metric.color}.main`, mb: 1 }}>
                    {metric.value}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {metric.metric}
                  </Typography>
                  <Chip 
                    label={metric.change} 
                    color={metric.color} 
                    size="small" 
                    variant="outlined"
                  />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

     

      {/* Analytics Features */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 500, mb: 3 }}>
          Analytics Features
        </Typography>
        <Grid container spacing={3}>
          {analyticsFeatures.map((feature, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card sx={{ height: '100%', border: '1px solid', borderColor: 'divider' }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    {feature.icon}
                    <Typography variant="h6" sx={{ ml: 1, fontWeight: 500 }}>
                      {feature.title}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {feature.description}
                  </Typography>
                  <List dense>
                    {feature.features.map((item, itemIndex) => (
                      <ListItem key={itemIndex} sx={{ py: 0.5, pl: 0 }}>
                        <ListItemIcon sx={{ minWidth: 30 }}>
                          <CheckIcon fontSize="small" color="success" />
                        </ListItemIcon>
                        <ListItemText primary={item} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Sample Analytics View */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 500, mb: 3 }}>
          Sample Analytics View
        </Typography>
        <Card sx={{ border: '1px solid', borderColor: 'divider' }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
              Question: "How satisfied are you with our product?"
            </Typography>
            
            {/* Sample Chart Data */}
            <Grid container spacing={3} sx={{ mt: 2 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom>Response Distribution</Typography>
                {[
                  { label: 'Very Satisfied', value: 45, color: 'success' },
                  { label: 'Satisfied', value: 32, color: 'info' },
                  { label: 'Neutral', value: 15, color: 'warning' },
                  { label: 'Dissatisfied', value: 6, color: 'error' },
                  { label: 'Very Dissatisfied', value: 2, color: 'error' }
                ].map((item, index) => (
                  <Box key={index} sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2">{item.label}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{item.value}%</Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={item.value} 
                      color={item.color}
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </Box>
                ))}
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom>Response Timeline</Typography>
                <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
                  <Typography variant="body2" color="text.secondary" align="center">
                    📈 Interactive timeline chart would appear here showing response patterns over time
                  </Typography>
                </Paper>
                
                <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>Key Insights</Typography>
                <List dense>
                  <ListItem sx={{ pl: 0 }}>
                    <ListItemIcon><InfoIcon color="info" fontSize="small" /></ListItemIcon>
                    <ListItemText primary="77% of respondents are satisfied or very satisfied" />
                  </ListItem>
                  <ListItem sx={{ pl: 0 }}>
                    <ListItemIcon><TrendingIcon color="success" fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Satisfaction increased 12% from last month" />
                  </ListItem>
                  <ListItem sx={{ pl: 0 }}>
                    <ListItemIcon><TimeIcon color="warning" fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Peak response time: 2-4 PM weekdays" />
                  </ListItem>
                </List>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>

      {/* Analytics Examples */}
      <Box sx={{ mb: 6 }}>
        <ScreenshotGallery
          title="Analytics Examples"
          columns={2}
          screenshots={[
            {
              src: chart,
              alt: "Chart Examples",
              caption: "Various chart types - bar charts, pie charts, and trend lines"
            },
            {
              src: result,
              alt: "Response Filtering",
              caption: "Advanced filtering options to segment your data"
            }
          ]}
        />
      </Box>

      {/* Report Types */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 500, mb: 3 }}>
          Report Types
        </Typography>
        <Grid container spacing={3}>
          {reportTypes.map((report, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card sx={{ height: '100%', border: '1px solid', borderColor: 'divider' }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
                    {report.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {report.description}
                  </Typography>
                  
                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                    Includes:
                  </Typography>
                  <List dense>
                    {report.includes.map((item, itemIndex) => (
                      <ListItem key={itemIndex} sx={{ py: 0.25, pl: 0 }}>
                        <ListItemIcon sx={{ minWidth: 25 }}>
                          <CheckIcon fontSize="small" color="success" />
                        </ListItemIcon>
                        <ListItemText primary={item} />
                      </ListItem>
                    ))}
                  </List>
                  
                  <Chip 
                    label={`Best for: ${report.bestFor}`} 
                    color="primary" 
                    variant="outlined" 
                    size="small"
                    sx={{ mt: 2 }}
                  />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Advanced Analytics */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 500, mb: 3 }}>
          Advanced Analytics Features
        </Typography>
        <Card sx={{ background: 'linear-gradient(135deg, #f6f9fc 0%, #f1f4f8 100%)' }}>
          <CardContent sx={{ p: 4 }}>
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
                  Statistical Analysis
                </Typography>
                <List>
                  <ListItem sx={{ pl: 0 }}>
                    <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                    <ListItemText primary="Confidence intervals and margins of error" />
                  </ListItem>
                  <ListItem sx={{ pl: 0 }}>
                    <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                    <ListItemText primary="Statistical significance testing" />
                  </ListItem>
                  <ListItem sx={{ pl: 0 }}>
                    <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                    <ListItemText primary="Correlation analysis between questions" />
                  </ListItem>
                  <ListItem sx={{ pl: 0 }}>
                    <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                    <ListItemText primary="Trend analysis and forecasting" />
                  </ListItem>
                </List>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
                  Segmentation & Filtering
                </Typography>
                <List>
                  <ListItem sx={{ pl: 0 }}>
                    <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                    <ListItemText primary="Demographic segmentation" />
                  </ListItem>
                  <ListItem sx={{ pl: 0 }}>
                    <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                    <ListItemText primary="Response source tracking" />
                  </ListItem>
                  <ListItem sx={{ pl: 0 }}>
                    <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                    <ListItemText primary="Time-based filtering" />
                  </ListItem>
                  <ListItem sx={{ pl: 0 }}>
                    <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                    <ListItemText primary="Custom field filtering" />
                  </ListItem>
                </List>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>

      {/* Export Options Table */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 500, mb: 3 }}>
          Export & Integration Options
        </Typography>
        <TableContainer component={Paper} sx={{ border: '1px solid', borderColor: 'divider' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Format</strong></TableCell>
                <TableCell><strong>Best For</strong></TableCell>
                <TableCell><strong>Includes</strong></TableCell>
                <TableCell><strong>Availability</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>CSV</TableCell>
                <TableCell>Spreadsheet analysis</TableCell>
                <TableCell>Raw data, timestamps, metadata</TableCell>
                <TableCell><Chip label="All Plans" color="success" size="small" /></TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Excel</TableCell>
                <TableCell>Advanced analysis</TableCell>
                <TableCell>Formatted data, charts, pivot tables</TableCell>
                <TableCell><Chip label="Premium" color="primary" size="small" /></TableCell>
              </TableRow>
              <TableRow>
                <TableCell>PDF Report</TableCell>
                <TableCell>Presentations</TableCell>
                <TableCell>Charts, summaries, insights</TableCell>
                <TableCell><Chip label="Premium" color="primary" size="small" /></TableCell>
              </TableRow>
              <TableRow>
                <TableCell>API Access</TableCell>
                <TableCell>Custom integrations</TableCell>
                <TableCell>Real-time data, webhooks</TableCell>
                <TableCell><Chip label="Enterprise" color="warning" size="small" /></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Best Practices */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 500, mb: 3 }}>
          Analytics Best Practices
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Alert severity="success">
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500 }}>
                Maximize Insights
              </Typography>
              <List dense>
                <ListItem sx={{ py: 0 }}>
                  <ListItemText primary="Monitor responses in real-time" />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                  <ListItemText primary="Look for patterns and trends" />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                  <ListItemText primary="Segment data by demographics" />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                  <ListItemText primary="Compare results over time" />
                </ListItem>
              </List>
            </Alert>
          </Grid>
          <Grid item xs={12} md={6}>
            <Alert severity="info">
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500 }}>
                Data Quality Tips
              </Typography>
              <List dense>
                <ListItem sx={{ py: 0 }}>
                  <ListItemText primary="Wait for sufficient sample size" />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                  <ListItemText primary="Check for response bias" />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                  <ListItemText primary="Validate unexpected results" />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                  <ListItemText primary="Document your methodology" />
                </ListItem>
              </List>
            </Alert>
          </Grid>
        </Grid>
      </Box>

      {/* Next Steps */}
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 500 }}>
          Ready to analyze your data?
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Start collecting responses and discover insights with our powerful analytics tools.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button variant="contained" href="/app/dashboard">
            View My Analytics
          </Button>
          <Button variant="outlined" href="/app/documentations/advanced/api-integration">
            API Integration
          </Button>
        </Box>
      </Box>

      {/* Bottom navigation CTA (Previous / Next) */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                          <Button  color="inherit"  variant="contained" href="/app/documentations/publishing-sharing/publishing-your-survey">
                            Previous
                          </Button>
                          {/* <Button variant="contained"  href="/app/documentations/analyzing-results/viewing-analytics">
                            Next
                          </Button> */}
                        </Box>
    </Box>
  );
};

export default ResponseAnalytics;